import { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '@hooks';
import { getFirebaseErrorMessage } from '@utils/firebaseErrors';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from || '/';

  const validate = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (error) {
      const firebaseError = error as FirebaseError;
      const message = firebaseError.code
        ? getFirebaseErrorMessage(firebaseError.code)
        : 'Login failed. Please try again.';
      toast(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-2 text-3xl font-bold text-gold">EstateMate</h1>
        <p className="mb-1 text-xxl font-bold text-gray-800">Login</p>
        <p className="mb-6 text-s text-gray-600">Sign in to continue to EstateMate</p>

        <form onSubmit={handleSubmit} noValidate>
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            error={errors.email}
            autoComplete="email"
          />

          <div className="mb-4">
            <label className="mb-2 block text-xs font-bold uppercase text-gray-700">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                className={`input-field pr-10 ${errors.password ? 'border-system-red focus:border-system-red focus:ring-system-red' : ''}`}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-system-red">{errors.password}</p>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            className="mt-2 w-full"
          >
            Login
          </Button>
        </form>
      </div>
    </div>
  );
};
